/**
 * Dynatrace Session Replay Integration
 *
 * This module provides utilities for managing Dynatrace session replay.
 * The script is loaded from the HTML header and is always available when the page loads.
 *
 * Key features:
 * - Session management (end sessions on logout)
 * - User identification for better tracking
 * - Automatic integration with login/logout flows
 *
 * Usage:
 * - Call identifyUser() on successful login to associate the session with a user
 * - Call endSession() on logout to end the current session
 * - Dynatrace automatically starts a new session on the next user interaction
 *
 */

import {
  DynaTraceWindow,
  PropertyObject,
  PropertyMap,
  PropertiesSendingReport,
} from 'global';

let dynaTraceWindow = window as DynaTraceWindow;

/**
 * Identifies the current user in Dynatrace for session tracking
 * @param userId - The unique identifier for the user
 */
export const identifyUser = (userId: string): void => {
  if (dynaTraceWindow.dtrum?.identifyUser) {
    dynaTraceWindow.dtrum.identifyUser(userId);
    console.log(`[Dynatrace] Identified user`);
  } else {
    console.warn(
      '[Dynatrace] identifyUser method not available. Dynatrace script may not be loaded.'
    );
  }
};

/**
 * Ends the current Dynatrace session
 * Should be called on logout to properly end the user's session
 * A new session will automatically start on the next user interaction
 */
export const endSession = (): void => {
  if (dynaTraceWindow.dtrum?.endSession) {
    dynaTraceWindow.dtrum.endSession();
    console.log('[Dynatrace] Session ended');
  } else {
    console.warn(
      '[Dynatrace] endSession method not available. Dynatrace script may not be loaded.'
    );
  }
};

/**
 * Sends custom session properties to Dynatrace
 * @param javaLongOrObject - Java long values or a PropertyObject
 * @param date - Date properties
 * @param shortString - String properties (for session-level custom strings)
 * @param javaDouble - Double/float numeric properties
 * @returns PropertiesSendingReport with sentCount and droppedCount
 *
 * Example usage:
 * sendSessionProperties(undefined, undefined, { userRole: 'admin', planType: 'premium' });
 */
export const sendSessionProperties = (
  javaLongOrObject?: PropertyObject | PropertyMap<number>,
  date?: PropertyMap<Date>,
  shortString?: PropertyMap<string>,
  javaDouble?: PropertyMap<number>
): PropertiesSendingReport => {
  if (dynaTraceWindow.dtrum?.sendSessionProperties) {
    const report = dynaTraceWindow.dtrum.sendSessionProperties(
      javaLongOrObject,
      date,
      shortString,
      javaDouble
    );
    console.log('[Dynatrace] Session properties sent');
    return report;
  } else {
    console.warn(
      '[Dynatrace] sendSessionProperties method not available. Dynatrace script may not be loaded.'
    );
    return { sentCount: 0, droppedCount: 0 };
  }
};

